import React, { useState } from "react";
import {
    Box, TextField, MenuItem, Typography, Slider, FormControl,
    Accordion, AccordionSummary, AccordionDetails, Chip, Stack,
    Button, IconButton
} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { EmbeddingModels, EmbeddingDimensions } from "../constants";
import { colorText } from "../App/theme";

const DatabaseSearchSettings = ({
    KBData,
    setKBData,
    searchEngine,
    setSearchEngine,
    embeddingModel,
    setEmbeddingModel,
    embeddingDimension,
    setEmbeddingDimension,
    embeddingConfigured,
    setSearchSettingsHasChanges
}) => {
    const [expanded, setExpanded] = useState(false);

    if (!searchEngine && !embeddingModel && !embeddingDimension) {
        return null;
    }

    return (
        <Accordion
            expanded={expanded}
            onChange={() => setExpanded(!expanded)}
            sx={{ mt: 3, mb: 2 }}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="database-search-content"
                id="database-search-header"
            >
                <Typography variant="subtitle1">Knowledge Base</Typography>
            </AccordionSummary>
            <AccordionDetails>
                {searchEngine && <Box sx={{ width: '100%', marginBottom: '16px' }}>
                    <TextField
                        select
                        label="Engine"
                        value={searchEngine}
                        onChange={(e) => {
                            setSearchEngine(e.target.value);
                            setSearchSettingsHasChanges(true);
                        }}
                        fullWidth={true}
                        variant="outlined"
                        SelectProps={{
                            renderValue: (selected) => (
                                <Box display="flex" key={selected}>
                                    {selected === 'IndexedDB' ? 'NoSQL + IndexedDB' : 'NoSQL + VectorDB'}
                                </Box>
                            )
                        }}
                    >
                        <MenuItem key={'VectorDB'} value={'VectorDB'}>
                            NoSQL + VectorDB - NoSQL backend with VectorDB for Terabyte scale search
                        </MenuItem>
                        <MenuItem key={'IndexedDB'} value={'IndexedDB'}>
                            NoSQL + IndexedDB - NoSQL backend with IndexedDB for local browser search
                        </MenuItem>
                    </TextField>
                </Box>}

                {embeddingModel && <Box sx={{ width: '100%', marginBottom: '16px' }}>
                    <TextField
                        select
                        label="Embedding Model"
                        value={embeddingModel || ''}
                        onChange={(model) => {
                            setEmbeddingModel(model?.target?.value);
                            setSearchSettingsHasChanges(true);
                        }}
                        fullWidth={true}
                        variant="outlined"
                        SelectProps={{
                            renderValue: (selected) => (
                                <Box display="flex" key={EmbeddingModels[selected]?.name}>
                                    {EmbeddingModels[selected]?.name || 'None'}
                                </Box>
                            )
                        }}
                    >
                        {Object.keys(EmbeddingModels).map((key) => (
                            <MenuItem key={key} value={key}>
                                {EmbeddingModels[key].name}
                            </MenuItem>
                        ))}
                        {embeddingConfigured && <MenuItem key={'none'} value={'none'}>
                            None
                        </MenuItem>}
                    </TextField>
                </Box>}

                {embeddingDimension && <Box sx={{ width: '100%', marginBottom: '16px' }}>
                    <TextField
                        select
                        label="Embedding Dimensions"
                        value={embeddingDimension || ''}
                        onChange={(model) => {
                            setEmbeddingDimension(model?.target?.value);
                            setSearchSettingsHasChanges(true);
                        }}
                        fullWidth={true}
                        variant="outlined"
                        SelectProps={{
                            renderValue: (selected) => (
                                <Box display="flex" key={selected}>
                                    {selected}
                                </Box>
                            )
                        }}
                    >
                        {EmbeddingDimensions.map((key) => (
                            <MenuItem key={key} value={key}>
                                {key}
                            </MenuItem>
                        ))}
                    </TextField>
                </Box>}

                {/* Knowledge Base Components Section */}
                {searchEngine === 'VectorDB' && <Box sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                    <Typography variant="subtitle1" sx={{ color: colorText, mb: 1, fontWeight: 600 }}>
                        Knowledge Base Components
                    </Typography>
                    <Typography variant="caption" sx={{ color: colorText, opacity: 0.7, display: 'block', mb: 2 }}>
                        Configure what information is injected into agent memory for each request
                    </Typography>

                    {/* Priority Items */}
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" sx={{ color: colorText, mb: 1 }}>
                            Priority Items
                        </Typography>
                        <Typography variant="caption" sx={{ color: colorText, opacity: 0.7, display: 'block', mb: 2 }}>
                            Items selected by itemId prefix, always included regardless of embeddings or semantic relevance
                        </Typography>

                        {/* Priority Items List */}
                        <Stack spacing={2}>
                            {(KBData?.options?.priorityItems || []).map((item, index) => (
                                <Box key={index} sx={{
                                    p: 2,
                                    border: '1px solid rgba(255,255,255,0.1)',
                                    borderRadius: 1,
                                    backgroundColor: 'rgba(255,255,255,0.02)'
                                }}>
                                    <Stack direction="row" spacing={2} alignItems="center">
                                        <TextField
                                            label="Prefix"
                                            value={item.prefix || ''}
                                            onChange={(e) => {
                                                const newItems = [...(KBData?.options?.priorityItems || [])];
                                                newItems[index] = { ...newItems[index], prefix: e.target.value };
                                                setKBData({
                                                    ...KBData,
                                                    options: {
                                                        ...KBData.options,
                                                        priorityItems: newItems
                                                    }
                                                });
                                                setSearchSettingsHasChanges(true);
                                            }}
                                            size="small"
                                            sx={{ flex: 1 }}
                                            placeholder="memory"
                                        />
                                        <Box sx={{ flex: 1.5 }}>
                                            <Typography variant="caption" sx={{ color: colorText }}>
                                                Limit: {item.limit || 100}
                                            </Typography>
                                            <Slider
                                                value={item.limit || 100}
                                                onChange={(event, newValue) => {
                                                    const newItems = [...(KBData?.options?.priorityItems || [])];
                                                    newItems[index] = { ...newItems[index], limit: newValue };
                                                    setKBData({
                                                        ...KBData,
                                                        options: {
                                                            ...KBData.options,
                                                            priorityItems: newItems
                                                        }
                                                    });
                                                    setSearchSettingsHasChanges(true);
                                                }}
                                                min={1}
                                                max={1000}
                                                step={1}
                                                valueLabelDisplay="auto"
                                                size="small"
                                            />
                                        </Box>
                                        <IconButton
                                            onClick={() => {
                                                const newItems = (KBData?.options?.priorityItems || []).filter((_, i) => i !== index);
                                                setKBData({
                                                    ...KBData,
                                                    options: {
                                                        ...KBData.options,
                                                        priorityItems: newItems
                                                    }
                                                });
                                                setSearchSettingsHasChanges(true);
                                            }}
                                            size="small"
                                            sx={{ color: 'rgba(255,100,100,0.8)' }}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </Stack>
                                </Box>
                            ))}

                            {/* Add New Priority Item Button */}
                            <Button
                                variant="outlined"
                                startIcon={<AddIcon />}
                                onClick={() => {
                                    const currentItems = KBData?.options?.priorityItems || [];
                                    const newItems = [...currentItems, { prefix: '', limit: 100 }];
                                    setKBData({
                                        ...KBData,
                                        options: {
                                            ...KBData.options,
                                            priorityItems: newItems
                                        }
                                    });
                                    setSearchSettingsHasChanges(true);
                                }}
                                size="small"
                                sx={{ alignSelf: 'flex-start', mt: 1 }}
                            >
                                Add Priority Item
                            </Button>
                        </Stack>
                    </Box>

                    {/* Vector Search Items */}
                    <Box>
                        <Typography variant="subtitle2" sx={{ color: colorText, mb: 1 }}>
                            Vector Search Items
                        </Typography>
                        <Typography variant="caption" sx={{ color: colorText, opacity: 0.7, display: 'block', mb: 2 }}>
                            Items with embeddings (generated from embedding template) matched against current conversation using semantic vector similarity
                        </Typography>

                        {/* Top K Slider */}
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" sx={{ color: colorText, mb: 1 }}>
                                Top K: {KBData?.options?.vectorDBTopK || 30}
                            </Typography>
                            <Slider
                                value={KBData?.options?.vectorDBTopK || 30}
                                onChange={(event, newValue) => {
                                    setKBData({
                                        ...KBData,
                                        options: {
                                            ...KBData.options,
                                            vectorDBTopK: newValue
                                        }
                                    });
                                }}
                                min={1}
                                max={30}
                                step={1}
                                valueLabelDisplay="auto"
                            />
                        </Box>

                        {/* Max Tokens Slider */}
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" sx={{ color: colorText, mb: 1 }}>
                                Max Tokens: {(KBData?.options?.vectorDBMaxTokens || 10000).toLocaleString()}
                            </Typography>
                            <Slider
                                value={KBData?.options?.vectorDBMaxTokens || 10000}
                                onChange={(event, newValue) => {
                                    setKBData({
                                        ...KBData,
                                        options: {
                                            ...KBData.options,
                                            vectorDBMaxTokens: newValue
                                        }
                                    });
                                }}
                                min={0}
                                max={50000}
                                step={10}
                                valueLabelDisplay="auto"
                            />
                        </Box>

                        {/* Min Score Slider */}
                        <Box>
                            <Typography variant="body2" sx={{ color: colorText, mb: 1 }}>
                                Min Score: {KBData?.options?.vectorDBMinScore || 0}%
                            </Typography>
                            <Slider
                                value={KBData?.options?.vectorDBMinScore || 0}
                                onChange={(event, newValue) => {
                                    setKBData({
                                        ...KBData,
                                        options: {
                                            ...KBData.options,
                                            vectorDBMinScore: newValue
                                        }
                                    });
                                }}
                                min={0}
                                max={100}
                                step={1}
                                valueLabelDisplay="auto"
                            />
                        </Box>
                    </Box>
                </Box>}
            </AccordionDetails>
        </Accordion>
    );
};

export default DatabaseSearchSettings;
